import { Button, Flex, Text, Box, DrawerHeader, DrawerBody, SimpleGrid, DrawerFooter, Field, Separator, Grid } from '@chakra-ui/react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { dbTimeToUserTime, Loading, priceFormatter, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useGetSingleOrder, useUpdateOrder } from '../../api/hooks/useOrdersSuperadmin';
import { useMeta } from '../../api/hooks/useMeta';
import { useGetAllDiscountCodes } from '../../api/hooks/useDiscountCodeSuperadmin';
import Decimal from 'decimal.js';

type Props = {
	orderID?: number;
};

type OrderForm = {
	orderStatus: OrderStatus;
	paymentStatus: PaymentStatus;
};

const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export const OrderDrawer = ({ orderID }: Props) => {
	const { t } = useTranslation();
	const updateOrder = useUpdateOrder();
	const { data: discountCodes } = useGetAllDiscountCodes();

	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();

	const { data: order, isFetching: isFetchingOrder } = useGetSingleOrder(orderID as number, {
		isEnabled: !!orderID,
	});

	const methods = useForm<OrderForm>({
		defaultValues: {
			...order,
		},
	});

	const {
		reset,
		setValue,
		control,
		formState: { dirtyFields },
	} = methods;

	useEffect(() => {
		if (order) {
			reset({
				...order,
			});
		}
	}, [order, reset]);

	function handleUpdateOrder(data: OrderForm): void {
		if (!order) {
			return;
		}
		const payload: OrderUpdateRequest = {
			orderID: order.orderID,
		};

		(Object.keys(dirtyFields) as Array<keyof OrderForm>).forEach(key => {
			const value = data[key];

			if (key === 'orderStatus') {
				payload[key] = value as OrderStatus;
			} else if (key === 'paymentStatus') {
				payload[key] = value as PaymentStatus;
			}
		});

		updateOrder.mutate(payload, {
			onSuccess: response => {
				toaster.create({ type: 'success', title: t('general.saved') });
				queryClient.invalidateQueries({ queryKey: ['orders-with-contact-counts'] });
				queryClient.invalidateQueries({ queryKey: ['orders'] });
				queryClient.setQueryData(['order', response.orderID], response);
				closeDrawer('orderDrawer');
			},
		});
	}

	const { meta } = useMeta('ordersService');

	const orderStatusCollection = [
		{
			label: meta?.orderStatus?.['draft'],
			value: 'draft',
		},
		{
			label: meta?.orderStatus?.['inProgress'],
			value: 'inProgress',
		},
		{
			label: meta?.orderStatus?.['completed'],
			value: 'completed',
		},
		{
			label: meta?.orderStatus?.['cancelled'],
			value: 'cancelled',
		},
	];

	const paymentStatusCollection = [
		{
			label: meta?.paymentStatus?.['awaitingPayment'],
			value: 'awaitingPayment',
		},
		{
			label: meta?.paymentStatus?.['awaitingInvoiceCreation'],
			value: 'awaitingInvoiceCreation',
		},
		{
			label: meta?.paymentStatus?.['paid'],
			value: 'paid',
		},
		{
			label: meta?.paymentStatus?.['cancelled'],
			value: 'cancelled',
		},
	];

	const discountCodeName = discountCodes?.data.discountCodes.find(d => d.discountCodeID === order?.discountCodeID)?.name;
	const orderStatus = useWatch({ control, name: 'orderStatus' });
	const paymentStatus = useWatch({ control, name: 'paymentStatus' });

	if (isFetchingOrder || !order) {
		return <Loading />;
	}

	return (
		<FormProvider {...methods}>
			<DrawerHeader width="100%" justifyContent={'space-between'}>
				<Text userSelect="text" fontWeight="bold" fontSize="lg">
					{t('general.order')} #{order.orderID}
				</Text>

				<Button onClick={() => window.open(`/orders/${orderID}`, '_blank')}>{t('general.open_receipt')}</Button>
			</DrawerHeader>

			<DrawerBody>
				<Flex as="form" id="orderForm" onSubmit={methods.handleSubmit(handleUpdateOrder)} flexDir="column" gap={6}>
					<SimpleGrid columns={2} gap={4}>
						<Field.Root>
							<Field.Label userSelect="text">{t('general.status')}</Field.Label>
							<label>
								<select
									style={{
										height: '40px',
										paddingInline: 'var(--tav-spacing-3)',
										borderRadius: 'var(--tav-radii-l2)',
										backgroundColor: 'white',
										border: 'var(--tav-colors-border) 1px solid',
										width: '100%',
									}}
									value={orderStatus}
									onChange={e => {
										const status = e.target.value as OrderStatus;
										setValue('orderStatus', status, { shouldDirty: true });
									}}
								>
									{orderStatusCollection.map(item => (
										<option
											disabled={item.value === 'draft' && order.orderStatus === 'completed'}
											key={item.value}
											value={item.value}
											data-cy={`cy-status-${item.value}`}
										>
											{item.label}
										</option>
									))}
								</select>
							</label>
						</Field.Root>
						<Field.Root>
							<Field.Label userSelect="text">{t('general.payment_status')}</Field.Label>
							<label>
								<select
									disabled={order.paymentMethod === 'paytrail'}
									style={{
										height: '40px',
										paddingInline: 'var(--tav-spacing-3)',
										borderRadius: 'var(--tav-radii-l2)',
										backgroundColor: 'white',
										border: 'var(--tav-colors-border) 1px solid',
										width: '100%',
										opacity: order.paymentMethod === 'paytrail' ? 0.5 : 1,
										cursor: order.paymentMethod === 'paytrail' ? 'not-allowed' : 'pointer',
									}}
									value={paymentStatus}
									onChange={e => {
										const status = e.target.value as PaymentStatus;
										setValue('paymentStatus', status, { shouldDirty: true });
									}}
								>
									{paymentStatusCollection.map(item => (
										<option key={item.value} value={item.value} data-cy={`cy-status-${item.value}`}>
											{item.label}
										</option>
									))}
								</select>
							</label>
						</Field.Root>
						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.product_info')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.orderRows?.[0]?.name}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.payment_method')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{meta?.paymentMethod?.[order.paymentMethod]}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.checkout_payment_status')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.checkoutPaymentStatus || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.discount_code')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.discountCodeID ? discountCodeName || t('general.code_has_been_deleted') : '-'}
							</Text>
						</Box>

						<Separator gridColumn="1 / -1" />
						<Box width="100%" gridColumn="1 / -1">
							{order.orderRows?.map((orderRow, index) => {
								const isEven = index % 2 === 0;

								return (
									<Grid
										key={orderRow.orderRowID}
										templateColumns="1fr 1fr 1fr 1fr"
										alignItems="center"
										gap={4}
										py={3}
										bg={isEven ? 'gray.50' : 'white'}
										borderRadius="md"
									>
										<Box>
											{index === 0 ? (
												<Text mb={4} fontWeight="semibold" userSelect="text" color="gray.700">
													{t('general.product')}
												</Text>
											) : null}
											<Text userSelect="text" color="gray.600">
												{orderRow.name}
											</Text>
										</Box>

										<Box>
											{index === 0 ? (
												<Text mb={4} textAlign="end" fontWeight="semibold" userSelect="text" color="gray.700">
													{t('general.total_without_vat')}
												</Text>
											) : null}
											<Text textAlign="end" userSelect="text" color="gray.600">
												{orderRow.totalWithoutVatRounded ? priceFormatter(orderRow.totalWithoutVatRounded) : '-'}
											</Text>
										</Box>

										<Box>
											{index === 0 ? (
												<Text mb={4} textAlign="end" fontWeight="semibold" userSelect="text" color="gray.700">
													{t('general.vat')}
												</Text>
											) : null}
											<Text textAlign="end" userSelect="text" color="gray.600">
												{priceFormatter(
													new Decimal(Number(orderRow.totalWithVatRounded) - Number(orderRow.totalWithoutVatRounded)).toDecimalPlaces(2).toNumber(),
												)}
											</Text>
										</Box>

										<Box>
											{index === 0 ? (
												<Text mb={4} textAlign="end" fontWeight="semibold" userSelect="text" color="gray.700">
													{t('general.total')}
												</Text>
											) : null}
											<Text textAlign="end" userSelect="text" color="gray.600">
												{orderRow.totalWithVatRounded ? priceFormatter(orderRow.totalWithVatRounded) : '-'}
											</Text>
										</Box>
									</Grid>
								);
							})}
						</Box>

						<Flex justifyContent={'space-between'} gridColumn="1 / -1">
							<Box>
								<Text fontWeight="semibold" color="gray.700"></Text>
								<Text userSelect="text" color="gray.600"></Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700"></Text>
								<Text userSelect="text" color="gray.600"></Text>
							</Box>
							<Box>
								<Text fontWeight="semibold" color="gray.700"></Text>
								<Text userSelect="text" color="gray.600"></Text>
							</Box>
							<Flex flexDirection="column">
								<Text color="gray.600" userSelect="text">
									{t('general.total_without_vat')}
								</Text>
								<Text color="gray.600" userSelect="text">
									{t('general.vat')}
								</Text>
								<Text color="gray.600" userSelect="text">
									{t('general.total')}
								</Text>
							</Flex>
							{order.totalWithVat && order.totalWithoutVat ? (
								<Flex flexDirection="column" textAlign={'end'} paddingBottom={4}>
									<Text color="gray.600" userSelect="text">
										{priceFormatter(order.totalWithoutVatRounded || '')}
									</Text>
									<Text color="gray.600" userSelect="text">
										{priceFormatter(new Decimal(Number(order.totalWithVatRounded) - Number(order.totalWithoutVatRounded)).toDecimalPlaces(2).toNumber())}
									</Text>
									<Text color="gray.600" userSelect="text">
										{priceFormatter(order.totalWithVatRounded || 0)}
									</Text>
								</Flex>
							) : null}
						</Flex>

						<Separator gridColumn="1 / -1" />

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.purchaser_name')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.purchaserFirstName} {order.purchaserLastName}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.email')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.email}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.company')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.companyName || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.business_id')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.businessID || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.vat_code')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.vatCode || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.postal_address')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.postalAddress || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.postal_code')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.postalCode || '-'}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.city')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.city || '-'}
							</Text>
						</Box>
						<Separator gridColumn="1 / -1" />
						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.time_created')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{dbTimeToUserTime(order.timeCreated, TIMEZONE)}
							</Text>
						</Box>

						<Box>
							<Text userSelect="text" fontWeight="semibold" color="gray.700">
								{t('general.time_modified')}
							</Text>
							<Text userSelect="text" color="gray.600">
								{order.timeModified ? dbTimeToUserTime(order.timeModified, TIMEZONE) : '-'}
							</Text>
						</Box>
					</SimpleGrid>
				</Flex>
			</DrawerBody>

			<DrawerFooter>
				<Button variant="outline" onClick={() => closeDrawer('orderDrawer')}>
					{t('general.cancel')}
				</Button>
				<Button type="submit" form="orderForm" loading={updateOrder.isPending}>
					{t('general.save')}
				</Button>
			</DrawerFooter>
		</FormProvider>
	);
};
